import { useEffect, useState } from 'react';
import { useCurrentStreak, useStreakShields } from '@busy/api';
import type { StreakRow, StreakShieldRow } from '@busy/api';
import type { StreakCelebrationKind } from '../components/StreakProtectionCelebrationModal';

// Detects newly-earned cooloffs and shields, queues celebration popups,
// and persists the last-seen state in localStorage so each earn moment
// only celebrates once per device.
//
// Two regimes:
//   First-ever load (no localStorage) — retroactively show ONE popup
//     per kind (cooloff, starter shields, anniversary shields, earned
//     shields collapsed to one). Caps the spam for a long-streaked
//     barber whose backfill migration just dropped a pile of new rows.
//   Subsequent loads — diff current state vs. last-seen, queue exact
//     deltas (each cooloff increase, each shield-grant moment grouped
//     by source + timestamp).

// v2 keys — bumped after fixing the "queue computed only once" bug. The
// old keys had been written by everyone on first load; with the fix,
// a user who'd already earned a cooloff between popup-firing-once and
// shipping the fix would never see a retroactive popup (their lastSeen
// already includes it). Rotating the key forces a clean retro pass on
// the next load so the missed earn moments fire once.
const COOLOFF_KEY = 'busy.streak.lastSeenCooloffs.v2';
const SHIELDS_KEY = 'busy.streak.lastSeenShieldIds.v2';

export type StreakCelebration = {
  kind: StreakCelebrationKind;
  count: number;
};

export function useStreakCelebrationQueue(opts: { ready: boolean }) {
  const streak = useCurrentStreak();
  const shields = useStreakShields();
  const [queue, setQueue] = useState<StreakCelebration[]>([]);

  // Recomputes the diff every time streak/shields data changes — so when
  // the user marks a lesson complete (which invalidates the streak cache
  // and triggers a refetch) and the bump trigger awarded a new cooloff,
  // the celebration fires immediately. Persists last-seen state right
  // away so subsequent renders that produce no new diff don't re-queue.
  useEffect(() => {
    if (!opts.ready) return;
    if (streak.loading || shields.loading) return;
    if (!streak.data) return;

    const items = computeCelebrations(streak.data, shields.data ?? []);
    // Always persist — even when items.length === 0 — so the first time
    // we see a clean diff we record it. Without this we'd recompute and
    // (depending on timing) re-fire the same retro popups.
    persistLastSeen(streak.data, shields.data ?? []);
    if (items.length > 0) {
      // Append rather than replace so a popup mid-flight isn't dropped
      // when fresh data arrives; the queue drains as the user dismisses
      // each modal.
      setQueue((q) => [...q, ...items]);
    }
  }, [opts.ready, streak.loading, shields.loading, streak.data, shields.data]);

  function dismissCurrent() {
    setQueue((q) => q.slice(1));
  }

  return {
    current: queue[0] ?? null,
    dismissCurrent,
  };
}

function computeCelebrations(
  streak: StreakRow,
  shields: StreakShieldRow[],
): StreakCelebration[] {
  const items: StreakCelebration[] = [];
  const lastCooloffs = lsGetNum(COOLOFF_KEY);
  const lastShieldIds = lsGetSet(SHIELDS_KEY);
  const isFirstLoad = lastCooloffs === null && lastShieldIds.size === 0;

  // ---- cooloffs ----------------------------------------------------------
  const currentCooloffs = streak.cooloffs_available;
  if (isFirstLoad) {
    if (currentCooloffs > 0) {
      items.push({ kind: 'cooloff', count: currentCooloffs });
    }
  } else if (lastCooloffs !== null && currentCooloffs > lastCooloffs) {
    items.push({ kind: 'cooloff', count: currentCooloffs - lastCooloffs });
  }

  // ---- shields -----------------------------------------------------------
  const newShields = shields.filter((s) => !lastShieldIds.has(s.id));
  if (isFirstLoad) {
    // Cap retro spam: one popup per source bucket.
    const starter = newShields.filter((s) => s.source === 'starter');
    const anniv = newShields.filter((s) => s.source === 'anniversary');
    const earned = newShields.filter((s) => s.source === 'earned');
    if (starter.length > 0) items.push({ kind: 'shield-grouped', count: starter.length });
    if (anniv.length > 0) items.push({ kind: 'shield-grouped', count: anniv.length });
    if (earned.length > 0) items.push({ kind: 'shield-earned', count: 1 });
  } else {
    // Real-time: group new shields by source + granted_at within a few
    // seconds (the grant_streak_shield RPC hands them out one-by-one but
    // a batch fires within a tight window, e.g. anniversary refresh).
    const groups = groupShieldsByGrantMoment(newShields);
    for (const g of groups) {
      if (g.source === 'starter' || g.source === 'anniversary' || g.length >= 2) {
        items.push({ kind: 'shield-grouped', count: g.length });
      } else {
        items.push({ kind: 'shield-earned', count: 1 });
      }
    }
  }

  return items;
}

type ShieldGroup = {
  source: StreakShieldRow['source'];
  length: number;
};

// Groups shields earned within 10 seconds of each other under the same
// source. Returns a list of groups with their total length.
function groupShieldsByGrantMoment(shields: StreakShieldRow[]): ShieldGroup[] {
  if (shields.length === 0) return [];
  const sorted = [...shields].sort(
    (a, b) => Date.parse(a.granted_at) - Date.parse(b.granted_at),
  );
  const groups: ShieldGroup[] = [];
  let currentSource = sorted[0]!.source;
  let currentTs = Date.parse(sorted[0]!.granted_at);
  let currentLen = 0;
  for (const s of sorted) {
    const ts = Date.parse(s.granted_at);
    if (s.source === currentSource && ts - currentTs < 10_000) {
      currentLen += 1;
      currentTs = ts;
    } else {
      groups.push({ source: currentSource, length: currentLen });
      currentSource = s.source;
      currentTs = ts;
      currentLen = 1;
    }
  }
  if (currentLen > 0) groups.push({ source: currentSource, length: currentLen });
  return groups;
}

function persistLastSeen(streak: StreakRow, shields: StreakShieldRow[]) {
  lsSet(COOLOFF_KEY, String(streak.cooloffs_available));
  lsSet(SHIELDS_KEY, JSON.stringify(shields.map((s) => s.id)));
}

// ─── localStorage helpers ─────────────────────────────────────────────────

function lsGetNum(key: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function lsGetSet(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x) => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function lsSet(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore — quota / private mode */
  }
}
